import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  fetchTenders, createTender as apiCreate, updateTender as apiUpdate,
  deleteTender as apiDelete, moveTender as apiMove, copyTender as apiCopy,
  updateTenderColor as apiUpdateColor,
} from '../services/api';
import { calculateStats, getAlerts, filterTenders } from '../utils/helpers';
import toast from 'react-hot-toast';

const TenderContext = createContext(null);

export const useTenders = () => {
  const context = useContext(TenderContext);
  if (!context) throw new Error('useTenders must be used within TenderProvider');
  return context;
};

export const TenderProvider = ({ children }) => {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '', status: '', field: '', win_status: '', priority: '',
  });
  const [selectedCategory, setSelectedCategory] = useState('active');
  const [editingTender, setEditingTender] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // ===== LOAD TENDERS =====
  const loadTenders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchTenders();
      setTenders(data);
    } catch (error) {
      toast.error('שגיאה בטעינת מכרזים');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenders();
  }, [loadTenders]);

  // ===== CRUD OPERATIONS =====
  const createTender = useCallback(async (tender) => {
    try {
      const created = await apiCreate(tender);
      setTenders(prev => [...prev, created]);
      toast.success('המכרז נוצר בהצלחה');
      return created;
    } catch (error) {
      toast.error('שגיאה ביצירת מכרז');
      throw error;
    }
  }, []);

  const updateTender = useCallback(async (rowKey, updates) => {
    try {
      const updated = await apiUpdate(rowKey, updates);
      setTenders(prev => prev.map(t => t.rowKey === rowKey ? updated : t));
      toast.success('המכרז עודכן בהצלחה');
      return updated;
    } catch (error) {
      toast.error('שגיאה בעדכון מכרז');
      throw error;
    }
  }, []);

  const deleteTender = useCallback(async (rowKey) => {
    try {
      await apiDelete(rowKey);
      setTenders(prev => prev.filter(t => t.rowKey !== rowKey));
      toast.success('המכרז נמחק בהצלחה');
    } catch (error) {
      toast.error('שגיאה במחיקת מכרז');
      throw error;
    }
  }, []);

  const moveTender = useCallback(async (rowKey, targetCategory) => {
    try {
      const moved = await apiMove(rowKey, targetCategory);
      setTenders(prev => prev.map(t => t.rowKey === rowKey ? moved : t));
      toast.success('המכרז הועבר בהצלחה');
      return moved;
    } catch (error) {
      toast.error('שגיאה בהעברת מכרז');
      throw error;
    }
  }, []);

  const copyTender = useCallback(async (rowKey, targetCategory) => {
    try {
      const copied = await apiCopy(rowKey, targetCategory);
      setTenders(prev => [...prev, copied]);
      toast.success('המכרז הועתק בהצלחה');
      return copied;
    } catch (error) {
      toast.error('שגיאה בהעתקת מכרז');
      throw error;
    }
  }, []);

  const updateRowColor = useCallback(async (rowKey, color) => {
    try {
      const updated = await apiUpdateColor(rowKey, color);
      setTenders(prev => prev.map(t => t.rowKey === rowKey ? updated : t));
    } catch (error) {
      toast.error('שגיאה בעדכון צבע');
    }
  }, []);

  // ===== COMPUTED VALUES =====
  const stats = calculateStats(tenders);
  const alerts = getAlerts(tenders);
  
  const filteredTenders = filterTenders(
    tenders.filter(t => {
      if (selectedCategory === 'all') return true;
      if (selectedCategory === 'clarification') {
        return t.category === 'active' && t.clarification_date;
      }
      if (selectedCategory === 'upcoming') {
        return t.category === 'active' && t.submission_date;
      }
      return t.category === selectedCategory;
    }),
    filters
  );

  // ===== FORM HELPERS =====
  const openCreateForm = useCallback(() => {
    setEditingTender(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((tender) => {
    setEditingTender(tender);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setEditingTender(null);
    setIsFormOpen(false);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: '', status: '', field: '', win_status: '', priority: '' });
  }, []);

  const value = {
    tenders, loading, filters, setFilters, clearFilters,
    selectedCategory, setSelectedCategory,
    filteredTenders, stats, alerts,
    createTender, updateTender, deleteTender,
    moveTender, copyTender, updateRowColor,
    editingTender, isFormOpen, openCreateForm, openEditForm, closeForm,
    loadTenders,
  };

  return (
    <TenderContext.Provider value={value}>
      {children}
    </TenderContext.Provider>
  );
};

export default TenderContext;